import React, { useMemo } from 'react';
import TableWindow from '../../components/Table/TableWindow';
import {
  AssemblyItem,
  GET_ASSEMBLIES_WATER_SUPPLIER,
  GET_ASSEMBLIES_COMPANY,
  GET_ASSEMBLY_IDS,
} from './Assemblies.query';
import ThirdPaneLayout, {
  MainColumn,
} from '../../components/Layout/ThirdPaneLayout';
import columnsWaterSupplier, { columnsCompany } from './Assemblies.columns';
import useTablePages from '../../hooks/useTablePages';
import PageBar from '../../components/PageBar/PageBar';

import FilterBar, {
  AssemblyFilters,
  defaultFilters,
  getDaysOperator,
} from './FilterBarAssembly';
import { useOrderBy } from '../../hooks/useOrderBy';
import { RouteComponentProps, withRouter } from 'react-router';
import AssemblyThirdPane from '../../components/BackflowAssembly/AssemblyThirdPane';

import useThirdPaneCallbacks from '../../hooks/useThirdPaneCallbacks';
import useMode from '../../hooks/useMode';
import useColumnConfiguration from '../../hooks/useColumnConfiguration';
import AssembliesActions from './AssembliesActions';
import useStoredFilters from '../../hooks/useStoredFilters';
import { toNumber } from '@ecosconnect/core/src/helpers/number';
import { Flex } from 'rebass';
import SelectableMap, { MapPoint } from '../../components/Map/SelectableMap';
import useRightPaneConfiguration from '../../hooks/useRightPaneConfiguration';
import { AssemblyResult } from '../../components/BackflowAssembly/AssemblyResultPicker';
import { PropertyType } from '../../components/WaterService/PropertyTypePicker';
import { getStringSearchOr } from '../../helpers/search';

const convertCompliantReason = (compliantReason?: string | string[]) => {
  let compliant: boolean | undefined;

  if (
    compliantReason === 'ALL_COMPLIANT' ||
    (Array.isArray(compliantReason) &&
      compliantReason.length === 1 &&
      compliantReason.includes('ALL_COMPLIANT'))
  ) {
    compliantReason = undefined;
    compliant = true;
  }
  if (
    compliantReason === 'ALL_NONCOMPLIANT' ||
    (Array.isArray(compliantReason) &&
      compliantReason.length === 1 &&
      compliantReason.includes('ALL_NONCOMPLIANT'))
  ) {
    compliantReason = undefined;
    compliant = false;
  }

  return {
    compliantReason:
      compliantReason && compliantReason.length > 0
        ? compliantReason
        : undefined,
    compliant,
  };
};

const convertLastResult = (lastResult?: AssemblyResult | AssemblyResult[]) => {
  if (!lastResult || lastResult.length === 0) {
    return undefined;
  }
  if (Array.isArray(lastResult)) {
    return {
      or: lastResult.map(convertLastResult),
    };
  }
  switch (lastResult) {
    case 'PASS':
    case 'FAIL':
      return {
        ['lastTest.finalResult']: lastResult,
      };
    case 'FAILPASS':
      return {
        ['lastTest.initialResult']: 'FAIL',
        ['lastTest.finalResult']: 'PASS',
      };
  }
};

const convertPropertyType = (propertyType?: PropertyType[]) => ({
  ['waterService.type']: propertyType,
});

const convertProtectionType = (protectionType?: string) => ({
  protectionType: protectionType === '' ? undefined : protectionType,
});

const convertDateFilters = (filters: AssemblyFilters) => {
  if (!filters.dateField) {
    return {};
  }

  const days = toNumber(filters.days);

  if (['daysUntilDue', 'daysPastDue'].includes(filters.dateField)) {
    if (typeof days === 'number') {
      const daysOperator = getDaysOperator(filters);
      if (daysOperator) {
        return {
          [filters.dateField]: { [daysOperator]: days },
        };
      }
    }
  } else if (filters.dateStart || filters.dateEnd) {
    if (filters.dateStart && filters.dateEnd) {
      return {
        [filters.dateField]: {
          between: [filters.dateStart, filters.dateEnd],
        },
      };
    }

    return {
      [filters.dateField]: {
        gte: filters.dateStart ? filters.dateStart : undefined,
        lte: filters.dateEnd ? filters.dateEnd : undefined,
      },
    };
  }

  return {};
};

export const stringSearchFields = [
  {
    field: 'serialNumber',
    label: 'Serial',
  },
  {
    field: 'waterService.meter_number',
    label: 'Meter',
  },
  {
    field: 'waterService.serviceAddresses.customer.profile.name',
    label: 'Customer',
  },
  {
    field: 'waterService.serviceAddresses.customer.vendorAccountNumber',
    label: null,
  },
  {
    field: 'waterService.site_number',
    label: 'Service',
  },
  {
    field: 'address.street1',
    label: 'Address',
  },
  {
    field: 'address.street2',
    label: null,
  },
  {
    field: 'location',
    label: 'Location',
  },
  {
    field: 'model.backflowAssemblyMake.name',
    label: 'Make',
  },
  {
    field: 'model.name',
    label: 'Model',
  },
  {
    field: 'waterService.pin',
    label: 'TRAC',
  },
  {
    field: 'tags.text',
    label: 'Tags',
  },
];

export const stringSearchFieldsCompany = [
  {
    field: 'serialNumber',
    label: 'Serial',
  },
  {
    field: 'waterService.meter_number',
    label: 'Meter',
  },
  {
    field: 'customerName',
    label: 'Customer',
  },
  // {
  //   field: 'waterService.serviceAddresses.customer.vendorAccountNumber',
  //   label: 'Customer Account',
  // },
  // {
  //   field: 'waterService.site_number',
  //   label: 'Service',
  // },
  {
    field: 'waterSupplier.profile.name',
    label: 'Supplier',
  },
  {
    field: 'address.street1',
    label: 'Address',
  },
  {
    field: 'address.street2',
    label: null,
  },
  {
    field: 'model.backflowAssemblyMake.name',
    label: 'Make',
  },
  {
    field: 'model.name',
    label: 'Model',
  },
  {
    field: 'waterService.pin',
    label: 'TRAC',
  },
];

type AssemblyDetailParams = {
  id?: string;
};

type AssemblyDetailProps = RouteComponentProps<AssemblyDetailParams>;

/**
 * @component Used for assemblies page
 */
function Assemblies({ match }: AssemblyDetailProps): JSX.Element {
  const mode = useMode();

  const searchFields =
    mode === 'company' ? stringSearchFieldsCompany : stringSearchFields;

  const convertStringSearch = (stringSearch?: string) => ({
    or: getStringSearchOr(searchFields, stringSearch),
  });

  const convertFilters = (filters: AssemblyFilters) => {
    const stringSearch = convertStringSearch(filters.stringSearch);
    const lastResult = convertLastResult(filters.lastResult);
    const and = [stringSearch, lastResult].filter(Boolean);
    return {
      status: filters.status,
      testStatus: filters.testStatus,
      ...convertCompliantReason(filters.compliantReason),
      ...convertDateFilters(filters),
      ...convertPropertyType(filters.propertyType),
      ...convertProtectionType(filters.protectionType),
      and: and.length > 0 ? and : undefined,
    };
  };

  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<AssemblyFilters>(
      convertFilters,
      'backflowAssemblies',
      defaultFilters
    );

  const availableColumns =
    mode === 'water-supplier' ? columnsWaterSupplier : columnsCompany;

  const { orderBy, onHeaderClick } = useOrderBy(
    'backflowAssemblies',
    availableColumns
  );

  const { rightPaneConfig, setRightPaneConfig } =
    useRightPaneConfiguration('backflowAssemblies');

  const {
    loading,
    error,
    itemIds,
    idItems,
    selectedIds,
    setSelectedIds,
    itemLoader,
    refetch,
  } = useTablePages<
    AssemblyItem,
    'id',
    { id: string; address?: { latitude?: number; longitude?: number } }
  >(
    'backflowAssemblies',
    GET_ASSEMBLY_IDS,
    {
      where: queryFilters,
      limit: null,
      orderBy,
      includeAddress: rightPaneConfig.visible,
    },
    mode === 'water-supplier'
      ? GET_ASSEMBLIES_WATER_SUPPLIER
      : GET_ASSEMBLIES_COMPANY,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
      orderBy,
    })
  );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'backflowAssemblies');

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(`/${useMode()}/assemblies`, itemIds, match.params.id);

  const points: MapPoint[] = useMemo(() => {
    return rightPaneConfig.visible
      ? idItems
          .filter(
            (item) =>
              typeof item.address?.longitude === 'number' &&
              typeof item.address?.latitude === 'number'
          )
          .map((item) => ({
            id: item.id,
            coordinates: [
              item.address?.longitude as number,
              item.address?.latitude as number,
            ],
          }))
      : [];
  }, [idItems]);

  return (
    <ThirdPaneLayout
      paneWidth={384 * (mode === 'company' ? 1 : 2)}
      paneVisible={!!match.params.id}
    >
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Assemblies">
          {/*<PortalWithState closeOnOutsideClick closeOnEsc>*/}
          {/*  {({ openPortal, closePortal, portal }) => (*/}
          {/*    <>*/}
          {/*      <Button*/}
          {/*        ml={3}*/}
          {/*        variant="primary"*/}
          {/*        icon="plus"*/}
          {/*        onClick={openPortal}*/}
          {/*      >*/}
          {/*        Add Assembly*/}
          {/*      </Button>*/}
          {/*      {portal(*/}
          {/*        <AssemblyFormModal action="create" onClose={closePortal} />*/}
          {/*      )}*/}
          {/*    </>*/}
          {/*  )}*/}
          {/*</PortalWithState>*/}
        </PageBar>
        <FilterBar
          filters={storedFilters}
          setFilters={setStoredFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <Flex flexDirection="row" flexGrow={1} flexShrink={1}>
            <TableWindow
              columns={visibleColumns}
              itemIds={itemIds}
              itemLoader={itemLoader}
              onSelectionChange={setSelectedIds}
              selectedIds={selectedIds}
              actionItems={<AssembliesActions selectedIds={selectedIds} />}
              activeIds={match.params.id ? [match.params.id] : undefined}
              onHeaderClick={onHeaderClick}
              orderBy={orderBy}
              onRowClick={onRowClick}
              columnConfiguration={columnConfiguration}
              setColumnConfiguration={setColumnConfiguration}
              rightPane={
                <SelectableMap
                  points={points}
                  onPointClick={onRowClick}
                  selectedIds={selectedIds}
                  onSelectionChange={setSelectedIds}
                  flexBasis={rightPaneConfig.width}
                />
              }
              rightPaneConfig={rightPaneConfig}
              setRightPaneConfig={setRightPaneConfig}
              rightPaneIcon="map"
            />
          </Flex>
        )}
      </MainColumn>
      <AssemblyThirdPane
        assemblyId={match.params.id}
        onPrev={prev}
        onRowClick={onRowClick}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
        refetch={refetch}
      />
    </ThirdPaneLayout>
  );
}

export default withRouter(Assemblies);
